import appConfig from "@appSrc/config";
import type { DefaultConfig } from "./types";
import { CurrencyUnit } from "@shared/generated/graphql/chargetrip";
import { StationDatabaseProvider, TimeConvention } from "@shared/types/config";
import { Products, Addons, SearchProvider } from "@shared/types/config";
import { merge } from "ts-deepmerge";
import { RouteName } from "@shared/types/routes";

const config: DefaultConfig = {
  application: {
    manifest: {
      title: "No-code solution starter",
      url: "https://go.chargetrip.com",
    },
    products: [Products.STATIONS, Products.ROUTING, Products.RANGE],
    addons: [Addons.SETTINGS, Addons.CLIMATE_REPORTING, Addons.SEARCH],
    initialization: {
      landingPage: RouteName.SEARCH,
      haptics: false,
      ui: {
        themes: {
          modes: ["auto", "light", "dark"],
          defaultMode: "auto",
        },
        footnotes: {
          isHidden: false,
        },
        onboarding: {
          type: ["modal"],
          isHidden: false,
          sortOrder: [
            "welcome",
            "slider",
            "vehicles",
            "vehicles-by-make",
            "cookies",
          ],
          slider: {
            isHidden: true,
            duration: 8_000,
          },
          cookies: {
            isHidden: false,
          },
          vehicles: {
            isHidden: false,
            skipButton: {
              isHidden: false,
            },
          },
          welcome: {
            isHidden: true,
          },
        },
        whatsNewModal: {
          isHidden: false,
        },
        headerLogo: "hidden",
        vehicle: {
          details: {
            isHidden: false,
          },
          formatting: {
            make: true,
            model: true,
            version: false,
          },
        },
        station: {
          name: {
            isHidden: false,
          },
          operator: {
            name: {
              isHidden: false,
            },
            icon: {
              isHidden: false,
            },
          },
        },
        powerGroups: {
          displayValues: {
            min: 3.4,
            max: 350,
          },
        },
      },
      cookies: {
        advertising: {
          defaultValue: false,
        },
        marketing: {
          defaultValue: false,
        },
        performance: {
          defaultValue: false,
        },
        analytics: {
          defaultValue: false,
        },
      },
      tracking: {
        isSentryEnabled: true,
        isDash0Enabled: false,
        gtmContainerId: "",
        gtmContainerIdV2: "",
        fathomId: "",
      },
      map: {
        cooperativeGestures: false,
        zoomRegion: new Map([
          [
            "default",
            // Amsterdam
            { zoomLevel: 12, position: { lng: 4.89707, lat: 52.377956 } },
          ],
        ]),
      },
      routeOverview: {
        isAlternativeRoutesEnabled: true,
      },
      station: {
        pricing: {
          defaultFixedKwhPrice: {
            currency: CurrencyUnit.EUR,
            vat: 21,
            prices: {
              "not-found": 0,
              slow: 0.8,
              fast: 0.8,
              turbo: 0.8,
            },
          },
        },
        database: StationDatabaseProvider.ECOMOVEMENT,
        showRankedOperatorsOnly: false,
      },
      defaultProductMode: "personal",
      productModes: ["personal"],
      routing: {
        alternativeRoutes: 2,
        charging: {
          mode: "OPTIMIZE_TRAVEL_TIME",
          operators: {
            mode: "none",
            preferred: {
              high: [],
              medium: [],
              low: [],
            },
            required: {
              high: [],
              medium: [],
              low: [],
            },
            excluded: [],
          },
        },
      },
      search: {
        stationsAround: {
          availableOnly: true,
          connectors: {
            isHidden: false,
          },
        },
        provider: SearchProvider.HERE,
      },
      localization: {
        currency: {
          isHidden: false,
          defaultValue: CurrencyUnit.EUR,
          supported: [CurrencyUnit.EUR, CurrencyUnit.USD],
          currencyLocaleMap: {
            "en-US": CurrencyUnit.USD,
          },
        },
        language: {
          defaultValue: "en-NL",
          supported: ["en-US", "en-NL"],
          hasBrowserLocale: false,
        },
        measurementSystem: {
          isHidden: false,
          defaultValue: "metric",
          supported: ["metric", "imperial"],
        },
        timeConvention: {
          isHidden: false,
          defaultValue: TimeConvention.HOUR_24,
          supported: [TimeConvention.HOUR_24, TimeConvention.HOUR_12],
        },
        temperatureConvention: {
          isHidden: false,
          defaultValue: "celsius",
          supported: ["celsius", "fahrenheit"],
        },
      },
      powerIndices: {
        100: {
          from: 3.4,
          to: 42,
        },
        200: {
          from: 43,
          to: 149,
        },
        300: {
          from: 150,
          to: Infinity,
        },
      },
      pricing: {
        type: "prices",
        fallback: {
          pricePerPower: {
            100: {
              price: 0.8,
              vat: 0,
              currency: CurrencyUnit.EUR,
            },
            200: {
              price: 0.8,
              vat: 0,
              currency: CurrencyUnit.EUR,
            },
            300: {
              price: 0.8,
              vat: 0,
              currency: CurrencyUnit.EUR,
            },
          },
        },
      },
    },
    contact: {
      resource: {
        type: "mail",
        value: "support@chargetrip.com",
      },
    },
  },
  map: {
    defaultZoom: 12,
    style: {
      light: {
        styleUrl: "mapbox://styles/chargetrip/cm1afokic02en01qyczgf1760",
        layers: {
          routing: {
            polyline: {
              fill: {
                width: 4,
                paint: "rgb(64, 149, 247)",
              },
              border: {
                width: 6,
                paint: "rgb(25, 81, 237)",
              },
            },
            alternativePolyline: {
              fill: {
                width: 4,
                paint: "rgb(180, 203, 251)",
              },
              border: {
                width: 6,
                paint: "rgb(150, 173, 221)",
              },
            },
            traffic: {
              unknown: {
                fill: {
                  width: 4,
                  paint: "rgb(64, 149, 247)", // Default blue
                },
                border: {
                  width: 6,
                  paint: "rgb(25, 81, 237)",
                },
              },
              low: {
                fill: {
                  width: 4,
                  paint: "rgb(64, 149, 247)", // Default blue
                },
                border: {
                  width: 6,
                  paint: "rgb(25, 81, 237)",
                },
              },
              medium: {
                fill: {
                  width: 4,
                  paint: "rgb(254, 154, 0)", // Orange
                },
                border: {
                  width: 6,
                  paint: "rgb(25, 81, 237)",
                },
              },
              high: {
                fill: {
                  width: 4,
                  paint: "rgb(195,66,63)", // Red
                },
                border: {
                  width: 6,
                  paint: "rgb(25, 81, 237)",
                },
              },
            },
            dashedPolyline: {
              fill: {
                width: 4,
                paint: "rgb(38, 35, 34)",
              },
              border: {
                width: 6,
                paint: "rgb(38, 35, 34)",
              },
            },
            alternativeDashedPolyline: {
              fill: {
                width: 4,
                paint: "rgb(38, 35, 34)",
              },
              border: {
                width: 6,
                paint: "rgb(38, 35, 34)",
              },
            },
            alternativeStations: {
              type: ["default"],
              default: {
                icon: {
                  size: ["interpolate", ["linear"], ["zoom"], 8, 1, 15, 1.25],
                },
              },
              selected: {
                icon: {
                  size: ["interpolate", ["linear"], ["zoom"], 8, 1, 15, 1],
                  offset: [0, -14],
                },
              },
            },
          },
          isoline: {
            polygon: {
              fill: {
                paint: "rgb(249, 77, 0)",
                opacity: 0.2,
              },
              border: {
                width: 1,
                paint: "rgb(249, 77, 0)",
              },
            },
          },
        },
        assets: {
          station: {
            cluster: {
              id: "CLUSTER-NEW",
              stretchableImage: {
                url: `/cluster/cluster.png`,
                stretchX: [[25, 35]],
                stretchY: [[0, 40]],
                content: [0, 0, 60, 40],
                pixelRatio: 2,
              },
              text: {
                paint: "rgb(255,255,255)",
                size: ["interpolate", ["linear"], ["zoom"], 8, 12, 10, 12],
                offset: [0, -0.2],
              },
              layout: {
                "icon-image": "CLUSTER-NEW",
                "icon-offset": [0, 0],
                "icon-text-fit": "width",
                "icon-text-fit-padding": [4, 8, 4, 8],
              },
            },
            availability: {
              supported: [
                {
                  status: "AVAILABLE",
                  legend: {
                    isHidden: false,
                  },
                },
                {
                  status: "CHARGING",
                  legend: {
                    isHidden: false,
                  },
                },
                {
                  status: "OUTOFORDER",
                  legend: {
                    isHidden: false,
                  },
                },
                {
                  status: "UNKNOWN",
                  legend: {
                    isHidden: false,
                  },
                },
              ],
            },
            markers: {
              100: {
                offset: [0, -16],
                legend: {
                  isHidden: false,
                },
              },
              200: {
                offset: [0, -16],
                legend: {
                  isHidden: false,
                },
              },
              300: {
                offset: [0, -16],
                legend: {
                  isHidden: false,
                },
              },
            },
          },
        },
      },
      dark: {
        styleUrl: "mapbox://styles/chargetrip/cmdsrpnl9006x01sh1dbubg9d",
        layers: {
          routing: {
            polyline: {
              fill: {
                width: 4,
                paint: "rgb(64, 149, 247)",
              },
              border: {
                width: 6,
                paint: "rgb(25, 81, 237)",
              },
            },
            alternativePolyline: {
              fill: {
                width: 4,
                paint: "rgb(180, 203, 251)",
              },
              border: {
                width: 6,
                paint: "rgb(150, 173, 221)",
              },
            },
            traffic: {
              unknown: {
                fill: {
                  width: 4,
                  paint: "rgb(64, 149, 247)", // Default blue
                },
                border: {
                  width: 6,
                  paint: "rgb(25, 81, 237)",
                },
              },
              low: {
                fill: {
                  width: 4,
                  paint: "rgb(64, 149, 247)", // Default blue
                },
                border: {
                  width: 6,
                  paint: "rgb(25, 81, 237)",
                },
              },
              medium: {
                fill: {
                  width: 4,
                  paint: "rgb(254, 154, 0)", // Orange
                },
                border: {
                  width: 6,
                  paint: "rgb(25, 81, 237)",
                },
              },
              high: {
                fill: {
                  width: 4,
                  paint: "rgb(195,66,63)", // Red
                },
                border: {
                  width: 6,
                  paint: "rgb(25, 81, 237)",
                },
              },
            },
            dashedPolyline: {
              fill: {
                width: 4,
                paint: "rgba(255, 255, 255, 1)",
              },
              border: {
                width: 6,
                paint: "rgba(255, 255, 255, 1)",
              },
            },
            alternativeDashedPolyline: {
              fill: {
                width: 4,
                paint: "rgb(38, 35, 34)",
              },
              border: {
                width: 6,
                paint: "rgb(38, 35, 34)",
              },
            },
            alternativeStations: {
              type: ["default"],
              default: {
                icon: {
                  size: ["interpolate", ["linear"], ["zoom"], 8, 1, 15, 1.25],
                },
              },
              selected: {
                icon: {
                  size: ["interpolate", ["linear"], ["zoom"], 8, 1, 15, 1],
                  offset: [0, 0],
                },
              },
            },
          },
          isoline: {
            polygon: {
              fill: {
                paint: "rgb(249, 77, 0)",
                opacity: 0.2,
              },
              border: {
                width: 1,
                paint: "rgb(249, 77, 0)",
              },
            },
          },
        },
        assets: {
          station: {
            cluster: {
              id: "CLUSTER-NEW",
              stretchableImage: {
                url: `/cluster/cluster.png`,
                stretchX: [[25, 35]],
                stretchY: [[0, 40]],
                content: [0, 0, 60, 40],
                pixelRatio: 2,
              },
              text: {
                paint: "rgb(255,255,255)",
                size: ["interpolate", ["linear"], ["zoom"], 8, 12, 10, 12],
                offset: [0, -0.2],
              },
              layout: {
                "icon-image": "CLUSTER-NEW",
                "icon-offset": [0, 0],
                "icon-text-fit": "width",
                "icon-text-fit-padding": [4, 8, 4, 8],
              },
            },
            availability: {
              supported: [
                {
                  status: "AVAILABLE",
                  legend: {
                    isHidden: false,
                  },
                },
                {
                  status: "CHARGING",
                  legend: {
                    isHidden: false,
                  },
                },
                {
                  status: "OUTOFORDER",
                  legend: {
                    isHidden: false,
                  },
                },
                {
                  status: "UNKNOWN",
                  legend: {
                    isHidden: false,
                  },
                },
              ],
            },
            markers: {
              100: {
                offset: [0, -16],
                legend: {
                  isHidden: false,
                },
              },
              200: {
                offset: [0, -16],
                legend: {
                  isHidden: false,
                },
              },
              300: {
                offset: [0, -16],
                legend: {
                  isHidden: false,
                },
              },
            },
          },
        },
      },
    },
  },
  features: {
    station: {
      sortOrder: [
        "chargeStats",
        "chargeAnimation",
        "evses",
        "amenities",
        "otherCards",
        "openingHours",
        "historicalAvailability",
        "truckAccessibility",
        "information",
        "identifiers",
      ],
      header: {
        truckAccessibilityTag: {
          isHidden: true,
        },
      },
      actions: {
        isHidden: false,
        share: {
          isHidden: false,
        },
        openInMaps: {
          isHidden: false,
        },
        useAsDestination: {
          isHidden: false,
        },
        report: {
          isHidden: false,
        },
      },
      chargeStats: {
        isHidden: false,
      },
      chargeAnimation: {
        isHidden: false,
        color: "--background-color-success",
      },
      evses: {
        isHidden: false,
        connectors: {
          availability: {
            isHidden: false,
          },
          price: {
            isHidden: false,
          },
          unknown: {
            isHidden: false,
          },
          indicators: {
            isHidden: false,
          },
        },
      },
      warnings: {
        isHidden: true,
        accessType: {
          isHidden: true,
        },
        vehicleRestrictions: {
          isHidden: true,
        },
        notOpenTwentyFourSeven: {
          isHidden: true,
        },
        onlyOneConnector: {
          isHidden: true,
        },
      },
      pricing: {
        isHidden: false,
        isExpandedByDefault: true,
        includeAdHocChargeCard: true,
        paymentMethods: {
          isHidden: false,
          disclaimers: [],
        },
        preferredChargeCards: {
          isHidden: true,
          add: {
            isHidden: true,
          },
          name: {
            isHidden: true,
          },
        },
      },
      supportedChargeCards: {
        isHidden: false,
        isExpandedByDefault: false,
        search: {
          isHidden: true,
        },
      },
      amenities: {
        isHidden: false,
        isExpandedByDefault: false,
      },
      openingHours: {
        isHidden: false,
        isExpandedByDefault: false,
      },
      historicalAvailability: {
        isHidden: false,
        isExpandedByDefault: true,
      },
      information: {
        isHidden: false,
        isExpandedByDefault: true,
        streetView: {
          isHidden: false,
        },
        operatorName: {
          isHidden: false,
        },
        openingHours: {
          isHidden: true,
        },
        address: {
          isHidden: false,
        },
        website: {
          isHidden: false,
        },
        parking: {
          isHidden: false,
        },
        reliabilityScore: {
          isHidden: false,
        },
        what3words: {
          isHidden: false,
        },
        chargeLeague: {
          isHidden: true,
        },
      },
      truckAccessibility: {
        isHidden: true,
        isExpandedByDefault: false,
        restrictions: {
          isHidden: false,
          trailerAccessible: {
            isHidden: false,
          },
          dangerousGoods: {
            isHidden: false,
          },
          rigidVehicles: {
            isHidden: false,
          },
          driveThrough: {
            isHidden: false,
          },
        },
        amenities: {
          isHidden: false,
          parking: {
            isHidden: false,
          },
          secureParking: {
            isHidden: false,
          },
          overnightParking: {
            isHidden: false,
          },
          wash: {
            isHidden: false,
          },
          repair: {
            isHidden: false,
          },
          dealership: {
            isHidden: false,
          },
        },
      },
      identifiers: {
        isHidden: false,
        isExpandedByDefault: false,
      },
    },
    settings: {
      primary: {
        general: {
          isHidden: true,
          productMode: {
            isHidden: true,
          },
          themeSupport: {
            isHidden: true,
          },
          hapticsSupport: {
            isHidden: true,
          },
        },
        faq: {},
        legend: {
          general: {
            isHidden: false,
          },
          markers: {
            availability: {
              default: {
                isHidden: false,
              },
              operatorSpecific: {
                isHidden: false,
              },
            },
            power: {
              isHidden: false,
              default: {
                isHidden: false,
              },
              operatorSpecific: {
                isHidden: false,
              },
            },
          },
          route: {},
          paymentMethods: {
            isHidden: false,
            arval: {
              isHidden: true,
            },
            creditcard: {
              isHidden: false,
            },
            debitcard: {
              isHidden: false,
            },
            qr: {
              isHidden: false,
            },
            sms: {
              isHidden: false,
            },
            apple: {
              isHidden: false,
            },
            google: {
              isHidden: false,
            },
            unknown: {
              isHidden: false,
            },
            rfid: {
              isHidden: false,
            },
            inCar: {
              isHidden: false,
            },
            bank: {
              isHidden: false,
            },
            paypal: {
              isHidden: false,
            },
            contact: {
              isHidden: false,
            },
            phone: {
              isHidden: false,
            },
            rsvp: {
              isHidden: true,
            },
            app: {
              isHidden: false,
            },
          },
        },
        whatsNew: {},
        contact: {},
      },
    },
    routing: {
      sortOrder: [
        "vehicle",
        "route",
        "charging",
        "driver",
        "operator",
        "amenities",
      ],
      cargo: {
        isHidden: true,
        delivery: {
          weight: {
            defaultValue: 0,
            minValue: 0,
            maxValue: Infinity,
          },
        },
        pickup: {
          isHidden: false,
          weight: {
            defaultValue: 0,
            minValue: 0,
            maxValue: Infinity,
          },
        },
      },
      departureTime: {
        isHidden: true,
      },
      vehicle: {
        isExpandedByDefault: true,
        batteryAtOrigin: {
          defaultValue: 80,
          minValue: 1,
          maxValue: 100,
        },
        batteryAtDestination: {
          defaultValue: 10,
          minValue: 1,
          maxValue: 80,
          description: {
            isHidden: true,
          },
        },
        occupants: {
          defaultValue: 1,
          minValue: 1,
          maxValue: 5,
        },
        drivingMode: {
          isHidden: true,
          defaultValue: "normal",
          options: ["comfort", "normal", "sport"],
        },
        climateControl: {
          defaultValue: true,
        },
        refrigeration: {
          isHidden: true,
          defaultValue: false,
        },
        additionalWeight: {
          weight: {
            defaultValue: 0,
            minValue: 0,
            maxValue: 1000,
          },
        },
      },
      truck: {
        isExpandedByDefault: true,
        batteryAtOrigin: {
          defaultValue: 80,
          minValue: 1,
          maxValue: 100,
        },
        batteryAtDestination: {
          defaultValue: 10,
          minValue: 1,
          maxValue: 60,
          description: {
            isHidden: true,
          },
        },
        occupants: {
          defaultValue: 1,
          minValue: 1,
          maxValue: 5,
        },
        drivingMode: {
          isHidden: true,
          defaultValue: "normal",
          options: ["comfort", "normal", "sport"],
        },
        climateControl: {
          defaultValue: true,
        },
        refrigeration: {
          isHidden: true,
          defaultValue: false,
        },
        additionalWeight: {
          weight: {
            defaultValue: 0,
            minValue: 0,
            maxValue: 24_000,
          },
        },
        trailer: {
          maxCount: 3,
          width: {
            defaultValue: 2.5,
            minValue: 0.5,
            maxValue: 5,
          },
          height: {
            defaultValue: 4,
            minValue: 0.5,
            maxValue: 5,
          },
          length: {
            defaultValue: 13.5,
            minValue: 1,
            maxValue: 30,
          },
        },
      },
      route: {
        isExpandedByDefault: true,
        weather: {
          defaultValue: "actual",
          options: ["actual", "summer", "winter"],
        },
        averageStopTime: {
          isHidden: true,
          time: {
            defaultValue: 0,
            minValue: 0,
            maxValue: 3600 * 24,
          },
          timeFormat: {
            defaultValue: "sec",
            options: ["sec", "min", "hrs"],
          },
        },
        avoidTolls: {
          isHidden: true,
          defaultValue: false,
        },
        avoidFerries: {
          isHidden: true,
          defaultValue: false,
        },
        trafficAware: {
          isHidden: true,
          defaultValue: false,
          soon: {
            isHidden: true,
          },
        },
        preferHighwayCharging: {
          defaultValue: true,
          isHidden: true,
        },
      },
      charging: {
        isHidden: true,
        isExpandedByDefault: true,
        alternativeStationRadius: {
          isHidden: true,
          defaultValue: 2000,
          minValue: 500,
          maxValue: 5000,
        },
        mode: {
          isHidden: true,
          defaultValue: "OPTIMIZE_TRAVEL_TIME",
          options: ["ALWAYS_TO_MAX_CHARGE", "OPTIMIZE_TRAVEL_TIME"],
        },
        minimumPower: {
          isHidden: true,
          defaultValue: 43,
        },
        riskMargin: {
          isHidden: true,
          defaultValue: 10,
          minValue: 10,
          maxValue: 60,
        },
      },
      operator: {
        isHidden: true,
        isExpandedByDefault: true,
        soon: {
          isHidden: true,
        },
        mode: {
          isHidden: true,
          isDisabled: true,
          defaultValue: "none",
          options: ["none", "preferred", "required"],
        },
        subscriptions: {
          isHidden: true,
          isDisabled: true,
        },
      },
      driver: {
        isHidden: true,
        isExpandedByDefault: true,
        soon: {
          isHidden: true,
        },
        drivingStyle: {
          isHidden: true,
          defaultValue: "neutral",
          options: ["neutral", "eco", "sport", "custom"],
        },
        maximumSpeed: {
          isHidden: true,
          defaultValue: 80,
          minValue: 0,
          maxValue: 240,
        },
        referenceSpeed: {
          isHidden: true,
          defaultValue: 0,
        },
      },
    },
    routeOverview: {
      sortOrder: [],
      alternativeStations: {
        toggle: {
          isHidden: false,
          isEnabledByDefault: false,
        },
        isNonPreferredOperatorsHidden: false,
        types: ["default"],
      },
      station: {
        operatorGroups: {},
        pricing: {
          isExpandedByDefault: false,
        },
        supportedChargeCards: {
          isExpandedByDefault: false,
        },
        amenities: {
          isExpandedByDefault: false,
        },
        openingHours: {
          isExpandedByDefault: false,
        },
        historicalAvailability: {
          isExpandedByDefault: false,
        },
        information: {
          isExpandedByDefault: false,
        },
        truckAccessibility: {
          isExpandedByDefault: false,
        },
        identifiers: {
          isExpandedByDefault: false,
        },
      },
      actions: {
        sendToPhone: {
          isHidden: false,
        },
        share: {
          isHidden: false,
        },
        amenityStop: {
          isHidden: true,
          maximumDistanceFromPath: {
            value: 2_500,
            unit: "meter",
          },
          timeWindowMargin: 900,
          // the default percentage of the route where the amenity stop is added
          defaultAmenityStopRoutePercentage: 50,
          // if a new amenity stop has the same amenity type and is very close in proximity to an existing amenity stop,
          // we delete the existing amenity stop before adding this new one
          // if thresholdPercentage is undefined, the feature is disabled
          replaceAmenityStopIfSimilar: {
            thresholdPercentage: 1,
          },
          sortOrder: [
            "restaurant",
            "coffee",
            "bathroom",
            "playground",
            "pharmacy",
            "museum",
            "park",
            "supermarket",
            "hotel",
            "shopping",
            "convenience_store",
            "bakery",
          ],
          amenities: {
            hotel: {
              isHidden: true,
            },
            shopping: {
              isHidden: true,
            },
            bathroom: {
              isHidden: false,
            },
            supermarket: {
              isHidden: true,
            },
            playground: {
              isHidden: false,
            },
            pharmacy: {
              isHidden: true,
            },
            coffee: {
              isHidden: false,
            },
            museum: {
              isHidden: true,
            },
            park: {
              isHidden: true,
            },
            restaurant: {
              isHidden: false,
            },
            bakery: {
              isHidden: true,
            },
            convenience_store: {
              isHidden: true,
            },
          },
        },
        aiEnhancedRoute: {
          isHidden: true,
          maxPoiDistanceKm: 50,
          minPoiSpacingKm: 50,
          // TODO: replace with more intutive controls, like spacing in km based on type
          hoursPerStop: {
            gastronomy: 2,
            cultural: 1,
            scenic: 1,
            custom: 2,
          },
        },
        alternativeStations: {
          isHidden: false,
        },
      },
      overview: {
        isHidden: false,
        trafficImpactDisclaimer: {
          isHidden: true,
        },
        extendedTravelTimes: {
          isHidden: true,
        },
      },
      location: {
        timeOfArrival: {
          isHidden: false,
        },
        warnings: {
          route: {
            isHidden: true,
            allowedTypes: [
              "origin_not_vehicle_accessible",
              "operators_preferred_not_available",
              "via_not_vehicle_accessible",
              "destination_not_vehicle_accessible",
              "final_soc_not_possible",
            ],
          },
          pricing: {
            isHidden: true,
          },
          notOpenTwentyFourSeven: {
            isHidden: true,
          },
        },
        waypoint: {
          isHidden: false,
          timelineItemDetails: {
            payload: {
              isHidden: false,
            },
            stopTime: {
              isHidden: false,
            },
          },
        },
        chargeStop: {
          isHidden: false,
          timelineItemDetails: {
            chargeTime: {
              isHidden: false,
            },
            chargeFrom: {
              isHidden: false,
            },
            pricing: {
              isHidden: false,
            },
          },
        },
      },
      chart: {
        isHidden: false,
        supported: ["elevation", "consumption"],
      },
      climateReport: {
        isHidden: false,
        negativeResult: {
          isHidden: false,
        },
        isExpandedByDefault: true,
      },
      about: {
        isHidden: false,
      },
      report: {
        isHidden: false,
      },
    },
    vehicle: {
      filters: {
        isHidden: true,
        sortOrder: ["EV", "HYBRID", "EU", "NA"],
        EV: {
          isHidden: false,
        },
        HYBRID: {
          isHidden: false,
        },
        EU: {
          isHidden: false,
        },
        NA: {
          isHidden: false,
        },
      },
      isGroupedByMake: false,
    },
    rangeSpider: {
      sortOrder: ["additionalVehicles", "vehicle", "range"],
      additionalVehicles: {
        isHidden: true,
        isExpandedByDefault: true,
        maxVehicles: 2,
      },
      vehicle: {
        isHidden: false,
        isExpandedByDefault: true,
        sortOrder: ["batteryAtOrigin", "occupants", "climate", "payload"],
        batteryAtOrigin: {
          isHidden: false,
          defaultValue: 100,
          minValue: 0,
          maxValue: 100,
        },
        occupants: {
          isHidden: false,
          defaultValue: 1,
        },
        climateControl: {
          isHidden: false,
          defaultValue: true,
        },
        additionalWeight: {
          isHidden: false,
          weight: {
            defaultValue: 0,
            minValue: 0,
            maxValue: 10_000,
          },
        },
      },
      range: {
        isHidden: false,
        isExpandedByDefault: true,
        sortOrder: ["weather", "roundTrip", "ferries"],
        weather: {
          isHidden: false,
          defaultValue: "actual",
          options: ["actual", "summer", "winter"],
        },
        roundTrip: {
          isHidden: false,
          defaultValue: false,
        },
        includeFerries: {
          isHidden: false,
          defaultValue: false,
        },
      },
    },
    rangeSpiderOverview: {
      stationsWithinRange: {
        isHidden: true,
        defaultValue: false,
      },
      goodToKnow: {
        isExpandedByDefault: true,
      },
      issues: {
        isExpandedByDefault: true,
      },
      legend: {
        isReversed: false,
      },
    },
  },
  filters: {
    sortOrder: [
      "preferences",
      "status",
      "power",
      "connectors",
      "vehicleTypes",
      "amenities",
    ],
    preferences: {
      isHidden: false,
      sortOrder: ["preferredOperator", "operatorIds"],
      preferredOperator: {
        isHidden: true,
        defaultValue: false,
      },
      operatorIds: [],
    },
    power: {
      isHidden: false,
      powerChargingDurationSteps: new Map([
        [3.4, 720],
        [22, 360],
        [42, 120],
        [100, 60],
        [150, 30],
        [350, 20],
      ]),
      defaultValue: {
        minimum: 3.4,
        maximum: Infinity,
      },
    },
    status: {
      isHidden: false,
      sortOrder: [
        "AVAILABLE",
        "BLOCKED",
        "CHARGING",
        "INOPERATIVE",
        "OUTOFORDER",
        "PLANNED",
        "RESERVED",
        "UNKNOWN",
      ],
      AVAILABLE: {
        defaultValue: false,
        isHidden: false,
      },
      BLOCKED: {
        defaultValue: false,
        isHidden: true,
      },
      CHARGING: {
        defaultValue: false,
        isHidden: true,
      },
      INOPERATIVE: {
        defaultValue: false,
        isHidden: true,
      },
      OUTOFORDER: {
        defaultValue: false,
        isHidden: true,
      },
      PLANNED: {
        defaultValue: false,
        isHidden: true,
      },
      REMOVED: {
        defaultValue: false,
        isHidden: true,
      },
      RESERVED: {
        defaultValue: false,
        isHidden: true,
      },
      UNKNOWN: {
        defaultValue: false,
        isHidden: true,
      },
    },
    connectors: {
      isHidden: false,
      sortOrder: [
        "IEC_62196_T1",
        "IEC_62196_T2",
        "IEC_62196_T1_COMBO",
        "IEC_62196_T2_COMBO",
        "CHADEMO",
        "TESLA_S",
      ],
      IEC_62196_T1: {
        isHidden: false,
        defaultValue: true,
      },
      IEC_62196_T2: {
        isHidden: false,
        defaultValue: true,
      },
      IEC_62196_T1_COMBO: {
        isHidden: false,
        defaultValue: true,
      },
      IEC_62196_T2_COMBO: {
        isHidden: false,
        defaultValue: true,
      },
      CHADEMO: {
        isHidden: false,
        defaultValue: true,
      },
      TESLA_S: {
        isHidden: false,
        defaultValue: true,
      },
    },
    amenities: {
      isHidden: false,
      sortOrder: [
        "hotel",
        "shopping",
        "bathroom",
        "supermarket",
        "playground",
        "pharmacy",
        "coffee",
        "museum",
        "park",
        "restaurant",
      ],
      hotel: {
        isHidden: false,
        defaultValue: false,
      },
      shopping: {
        isHidden: false,
        defaultValue: false,
      },
      bathroom: {
        isHidden: false,
        defaultValue: false,
      },
      supermarket: {
        isHidden: false,
        defaultValue: false,
      },
      playground: {
        isHidden: false,
        defaultValue: false,
      },
      pharmacy: {
        isHidden: false,
        defaultValue: false,
      },
      coffee: {
        isHidden: false,
        defaultValue: false,
      },
      museum: {
        isHidden: false,
        defaultValue: false,
      },
      park: {
        isHidden: false,
        defaultValue: false,
      },
      restaurant: {
        isHidden: false,
        defaultValue: false,
      },
    },
    vehicleTypes: {
      isHidden: true,
      sortOrder: ["VAN", "RIGID", "TRUCK_WITH_TRAILER"],
      VAN: {
        isHidden: false,
        defaultValue: false,
      },
      RIGID: {
        isHidden: false,
        defaultValue: false,
      },
      TRUCK_WITH_TRAILER: {
        isHidden: false,
        defaultValue: false,
      },
    },
  },
};

export const CONFIG = merge.withOptions(
  { mergeArrays: false, allowUndefinedOverrides: false },
  config,
  appConfig,
) as DefaultConfig;
